export interface LoadingBarHandle {
  setProgress: (t: number) => void;
  dismiss: () => Promise<void>;
}

const NOOP: LoadingBarHandle = {
  setProgress: () => {},
  dismiss: () => Promise.resolve()
};

const FADE_MS = 280;
// Target asymptote — the simulated curve approaches but never exceeds this.
// 0.92 = "we've made progress, but we're not claiming we're done" until
// the real-load completion triggers dismiss().
const SIM_CAP = 0.92;
// Time constant for the asymptotic curve `1 - exp(-elapsed / TAU)`. Tuned so
// the bar feels alive on slow connections (visible motion in first ~2s) but
// doesn't overshoot real progress on fast ones (asymptote stays well above
// fast-load completion). TAU=8s means at t=2s simulated=0.22, t=4s=0.39,
// t=8s=0.63, t=16s=0.86. Real progress events override these whenever
// they're higher.
const SIM_TAU_MS = 8000;
// Smoothing — fraction of the gap to target consumed per rAF tick.
// 0.08 ≈ ~5 frames to close 50% of any gap. Enough for visible motion,
// gentle enough to absorb the jumpy real-progress events without snapping.
const SMOOTH_ALPHA = 0.08;
// Final-snap animation when actual load completes.
const FINISH_MS = 220;

export function attachLoadingBar(): LoadingBarHandle {
  const screenEl = document.getElementById('loading-screen');
  const barEl = document.getElementById('loading-bar');
  if (!screenEl || !barEl) return NOOP;
  // Local refs to satisfy the closure-narrowing limits — TS doesn't carry
  // the null-check through nested function scopes.
  const screen: HTMLElement = screenEl;
  const bar: HTMLElement = barEl;

  let dismissed = false;
  let realProgress = 0;
  let displayed = 0;
  let raf = 0;
  const startedAt = performance.now();

  // The CSS already declares `transition: width 180ms cubic-bezier(...)` on
  // the bar, but we drive the value smoothly from rAF here instead of
  // relying on the CSS transition. Disable the CSS transition for our
  // controlled phase; we'll re-enable for the final snap.
  bar.style.transition = 'none';

  function tick() {
    if (dismissed) return;
    const elapsed = performance.now() - startedAt;
    const simulated = Math.min(SIM_CAP, 1 - Math.exp(-elapsed / SIM_TAU_MS));
    const target = Math.max(realProgress, simulated);
    // Smooth approach so jumpy real-progress events don't visually snap.
    displayed += (target - displayed) * SMOOTH_ALPHA;
    bar.style.width = `${(displayed * 100).toFixed(2)}%`;
    raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);

  return {
    setProgress: (t) => {
      if (dismissed) return;
      const clamped = Math.max(0, Math.min(1, t));
      // Monotonic: never go backwards. Real-progress sources can occasionally
      // re-emit lower values during retries; the visual bar should not.
      realProgress = Math.max(realProgress, clamped);
    },
    dismiss: () =>
      new Promise<void>((resolve) => {
        if (dismissed) {
          resolve();
          return;
        }
        dismissed = true;
        cancelAnimationFrame(raf);
        // Re-enable CSS transition for a smooth final-snap to 100%.
        bar.style.transition = `width ${FINISH_MS}ms cubic-bezier(.4,0,.2,1)`;
        bar.style.width = '100%';
        window.setTimeout(() => {
          screen.style.opacity = '0';
          window.setTimeout(() => {
            screen.remove();
            resolve();
          }, FADE_MS);
        }, FINISH_MS + 20);
      })
  };
}
